'use client';

/**
 * WHAT COVERS THE CANVAS, for the ad slot (2026-09-30 fix round, fix 1a).
 *
 * The visual pass opened `/workspace?dialog=admin&tab=ads` with ads on for
 * the viewer: the top window's title-bar slot stayed `data-ad-state="shown"`
 * although the element at the ad's centre was the Admin dialog, and the load
 * counted a serve (and possibly an impression) for an ad nobody could see.
 * The slot only knew about windows (`useTopAdWindowId`), full screen and the
 * Stage overview; the full-page dialogs live in React context
 * (FullscreenNavProvider) and Discover in its own module store, and neither
 * reached node-frame.tsx.
 *
 * `useCanvasCovered` is that one answer, read by node-frame.tsx for EVERY
 * placement it draws (title bar, the phone's app bar, the desktop full-screen
 * bar), so while it is true the slot is inactive and makes no request at all:
 *
 *   - a workspace dialog is open (`WorkspaceDialogOpenContext`, provided by
 *     FullscreenNavProvider): Admin, Settings, Account, Discover's dialog
 *     entry, Notifications, a preview, sign-in. `fixed inset-0` over a
 *     backdrop, so it covers everything, full screen included;
 *   - Discover is open over the canvas area (`useDiscoverCoversCanvas`); the
 *     desktop column docked beside your work does not cover it;
 *   - Stage is on (`overview`): the desktop overview covers every window with
 *     its card cover, and below md it is the Stage sheet over the canvas;
 *   - below md (`narrow`), the left or top edge panel is open: there they are
 *     sheets over most of the viewport on a backdrop (panel-shell.tsx). On a
 *     desktop they are cards at the edge and do not cover the canvas.
 *
 * Anything else that happens to lie over an ad (another window, a popover, a
 * modal this list does not name) is caught by the second line of defence in
 * components/ads/ad-trackers.ts: nothing is counted unless hit tests at the
 * ad's centre and two inner points land inside the ad itself.
 *
 * The dialog flag is a separate, dependency-free context rather than a read
 * of `useFullscreenNav()`, so node-frame.tsx does not import the dialog
 * registry (Admin, Settings, Discover and the rest) and every jsdom test that
 * renders a NodeFrame stays as light as it was.
 */

import { createContext, useContext, useSyncExternalStore } from 'react';

import { useDiscoverCoversCanvas } from './discover/discover-events';
import { getWorkspacePanelState, subscribeWorkspacePanelState } from './panels/panel-events';

/** True while FullscreenNavProvider shows a dialog over the workspace. */
export const WorkspaceDialogOpenContext = createContext(false);

/** A workspace dialog (the FullscreenNav registry's kind) is open. */
export function useWorkspaceDialogOpen(): boolean {
  return useContext(WorkspaceDialogOpenContext);
}

/** The edge panels that are sheets over the canvas below md. */
const SHEET_PANEL_IDS = ['left', 'top'] as const;

function subscribePanels(cb: () => void): () => void {
  return subscribeWorkspacePanelState(() => cb());
}
const panelSheetSnapshot = () => SHEET_PANEL_IDS.some((id) => getWorkspacePanelState(id));
const noPanelSnapshot = () => false;

export interface CanvasCoverInput {
  /** Below md: the edge panels are sheets over the canvas there. */
  narrow: boolean;
  /** The store's `overviewMode`: Stage (the desktop overview, or the phone's Stage sheet). */
  overview: boolean;
}

/** Something covers the canvas on this device, so no ad slot on it may be live. */
export function useCanvasCovered({ narrow, overview }: CanvasCoverInput): boolean {
  const dialog = useContext(WorkspaceDialogOpenContext);
  const discover = useDiscoverCoversCanvas();
  const panelSheet = useSyncExternalStore(subscribePanels, panelSheetSnapshot, noPanelSnapshot);
  return dialog || discover || overview || (narrow && panelSheet);
}
